import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

/**
 * Politique de confidentialite - Ember (conforme RGPD, beta gratuite).
 * Le traitement des reponses + opt-ins releve de l'art 9 RGPD (vie sexuelle).
 * Base legale : consentement explicite (art 6.1.a + 9.2.a).
 */
export function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
        Retour
      </Button>

      <header className="space-y-2">
        <h1 className="text-3xl font-bold text-primary">Politique de confidentialité</h1>
        <p className="text-sm text-muted-foreground">
          Dernière mise à jour : 16 mai 2026 - Version beta
        </p>
      </header>

      <section className="space-y-3">
        <p>
          On prend la confidentialité de tes données au sérieux. Cette page t'explique précisément
          ce qu'on collecte, pourquoi, combien de temps on garde, et comment tu peux exercer tes
          droits. Pas de jargon inutile.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">1. Responsable de traitement</h2>
        <p>Le responsable du traitement de tes données personnelles est :</p>
        <ul className="list-none space-y-1">
          <li>
            <strong>Nicolas DODEMANT</strong> (entreprise individuelle), exerçant sous la marque{' '}
            <strong>Black Potion</strong>
          </li>
          <li>Adresse : {'{{ADRESSE}}'}</li>
          <li>
            Contact :{' '}
            <a href="mailto:kavaliero37@gmail.com" className="text-primary underline">
              {'kavaliero37@gmail.com'}
            </a>
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">2. Données collectées</h2>
        <p>Ember collecte uniquement ce qui est nécessaire au fonctionnement du jeu :</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Le pseudo</strong> que tu choisis (libre, peut être un nom inventé ou un
            surnom).
          </li>
          <li>
            <strong>Tes choix d'opt-ins</strong> (catégories de questions activées : émotion, sexe,
            défi). Ces choix concernent ta vie intime et sexuelle, et constituent à ce titre des{' '}
            <strong>données dites sensibles</strong> au sens de l'article 9 du RGPD.
          </li>
          <li>
            <strong>Tes réponses libres aux questions du jeu</strong>. Ces réponses peuvent contenir
            des informations personnelles, intimes ou sensibles. Tu choisis ce que tu écris.
          </li>
          <li>
            <strong>Des données techniques</strong> dans nos logs serveur : adresse IP, user-agent
            (navigateur, OS), horodatage des requêtes. Ces informations sont utilisées pour le debug
            technique et la prévention d'abus.
          </li>
          <li>
            <strong>Des données stockées localement</strong> dans ton navigateur (localStorage) :
            ton pseudo et un identifiant de joueur lié à la session en cours. Ces données restent
            sur ton appareil et ne sont pas transmises à des tiers.
          </li>
        </ul>
        <p>
          Ember <strong>ne collecte pas d'adresse email</strong>, ne demande pas de mot de passe et
          n'a pas de système d'authentification fort en beta.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">3. Finalités du traitement</h2>
        <p>On utilise tes données uniquement pour :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            faire fonctionner le jeu (créer une session, t'envoyer les questions adaptées à tes
            opt-ins, synchroniser tes réponses avec les autres joueurs) ;
          </li>
          <li>déboguer les problèmes techniques et améliorer la stabilité du service ;</li>
          <li>prévenir et détecter les abus (spam, harcèlement, tentatives d'attaque).</li>
        </ul>
        <p>
          On <strong>n'utilise pas</strong> tes données à des fins publicitaires, on ne les revend
          pas, on ne fait pas de profilage, et on n'utilise aucun outil de tracking analytique tiers
          (pas de Google Analytics, pas de Meta Pixel, etc.).
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">4. Base légale</h2>
        <p>
          Le traitement de tes données repose sur ton <strong>consentement explicite</strong>{' '}
          (article 6.1.a du RGPD), donné lors de la vérification d'âge et de la création de session.
        </p>
        <p>
          Pour les données relatives à ta vie sexuelle (choix d'opt-ins et contenu des réponses sur
          les catégories sexe / défi), on s'appuie sur ton <strong>consentement explicite</strong>{' '}
          au sens de l'article 9.2.a du RGPD, matérialisé par l'activation manuelle de ces opt-ins.
        </p>
        <p>
          Tu peux retirer ton consentement à tout moment en cessant d'utiliser le service et en
          demandant la suppression de tes données (voir section 7).
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">5. Durée de conservation</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>Sessions de jeu et réponses associées :</strong> conservées 30 jours après la
            dernière activité de la session, puis supprimées automatiquement.
          </li>
          <li>
            <strong>Logs techniques (IP, user-agent) :</strong> conservés 30 jours pour permettre le
            debug et la sécurité, puis purgés.
          </li>
          <li>
            <strong>Données locales (localStorage) :</strong> conservées tant que tu ne vides pas le
            stockage de ton navigateur. Tu peux les effacer toi-même à tout moment via les
            paramètres de ton navigateur.
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">6. Destinataires des données</h2>
        <p>Tes données sont accessibles uniquement par :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Nicolas DODEMANT, en qualité de responsable de traitement et seule personne ayant un
            accès technique aux bases de données ;
          </li>
          <li>
            <strong>OVHcloud</strong>, hébergeur du service, qui stocke physiquement les données sur
            ses serveurs.
          </li>
        </ul>
        <p>
          <strong>Aucun transfert de données hors de l'Union européenne</strong> n'est effectué. Les
          serveurs sont localisés en France (Paris).
        </p>
        <p>
          On ne partage tes données avec <strong>aucun tiers commercial</strong>, aucun partenaire
          publicitaire, aucun courtier en données.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">7. Tes droits</h2>
        <p>Conformément au RGPD, tu disposes des droits suivants :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>Droit d'accès</strong> aux données te concernant ;
          </li>
          <li>
            <strong>Droit de rectification</strong> en cas d'erreur ;
          </li>
          <li>
            <strong>Droit à l'effacement</strong> (droit à l'oubli) ;
          </li>
          <li>
            <strong>Droit d'opposition</strong> au traitement ;
          </li>
          <li>
            <strong>Droit à la portabilité</strong> (récupération de tes données dans un format
            lisible) ;
          </li>
          <li>
            <strong>Droit de retirer ton consentement</strong> à tout moment ;
          </li>
          <li>
            <strong>Droit à la limitation</strong> du traitement dans certaines situations.
          </li>
        </ul>
        <p>
          Pour exercer un de ces droits, écris-nous à{' '}
          <a href="mailto:kavaliero37@gmail.com" className="text-primary underline">
            {'kavaliero37@gmail.com'}
          </a>{' '}
          en précisant ta demande et un moyen de t'identifier (par exemple le pseudo utilisé et le
          code de session concernée). On te répondra dans un délai maximum d'un mois.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">8. Réclamation auprès de la CNIL</h2>
        <p>
          Si tu estimes que tes droits ne sont pas respectés, tu peux introduire une réclamation
          auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :
        </p>
        <ul className="list-none pl-6 space-y-1">
          <li>3 place de Fontenoy - TSA 80715 - 75334 PARIS CEDEX 07</li>
          <li>
            Site :{' '}
            <a
              href="https://www.cnil.fr"
              className="text-primary underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.cnil.fr
            </a>
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">9. Cookies et traceurs</h2>
        <p>
          Ember n'utilise <strong>aucun cookie tiers</strong> ni outil de tracking publicitaire ou
          analytique. Seul le <strong>localStorage</strong> de ton navigateur est utilisé pour
          mémoriser ton pseudo et l'identifiant de session en cours, dans un but strictement
          fonctionnel.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">10. Sécurité</h2>
        <p>
          On met en oeuvre des mesures techniques raisonnables pour protéger tes données
          (chiffrement HTTPS, base de données isolée, accès restreint). Cela dit, Ember est en beta
          : on ne peut pas garantir une sécurité absolue. Évite d'écrire dans tes réponses des
          informations dont la fuite serait grave pour toi (noms complets, adresses, etc.).
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">11. Modification de cette politique</h2>
        <p>
          Cette politique peut évoluer, notamment au cours de la beta. La date de dernière mise à
          jour est indiquée en haut de la page. Les changements significatifs te seront signalés via
          une notification dans l'application.
        </p>
      </section>
    </div>
  );
}
